import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import DynamicModalData from "@/models/dynamicmodaldata"
import moment from "moment"
import TableData from "@/models/tabledata"
import utils from "@/utils/utils"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export interface Peittoalueet {
    koordinaatit: GeometryData;
    julkaisuaika: string;
}

export interface TVSaatavuus {
    // Palautettavia tietoja(listaus lähettimistä): lähettimen tunniste, lähettimen nimi, lähettimen suunta valitusta pisteestä, lähettimen etäisyys valitusta pisteestä
    id: number;
    idlist: Array<number>;
    nimi: string;
    suunta: string;
    etaisyys: number;
    kanavalkm: number;
    julkaisuaika: string;
}

export interface TVKanavat {
    // nimi, kanavanumero, kanavanippu, lähetyskanava/taajuus
    nimi: string;
    kanavaNumero: string;
    kanavaNippu: string;
    taajuus: string;
}

export interface PalvelutarjojaJaKanavat {
    // palvelutarjoajan nimi, linkki palveluntarjoajan verkkosivuille, lähettimen kanavalistaus (nimi, kanavanumero, kanavanippu, lähetyskanava/taajuus)
    nimi: string;
    urlfi: string;
    urlsv: string;
    urlen: string;
    kanavalista: Array<TVKanavat>;
}

export class ConvertTVKanavat {
    public static toTable(tvKanavat: Array<TVKanavat>): Array<Array<string>> {
        const json = JSON.stringify(tvKanavat)
        return JSON.parse(json)
    }
}

export interface RadioKuuluvuus {
    asema: string;
    taajuus: string;
    lahetin: string;
    palveluntarjoaja: string;
    julkaisuaika: string;
}

export default class TVJaRadioService {
    // #region TVPeittoalueet

    /** Haetun datan käsittelyä: TV ja Radio Peittoalueet
     Näytetään Alueita (yksi koordinaattitieto jossa yhdistettynä useita alueita)
     rightslotissa näytetään kiinteä infoteksti + julkaisuaika */
    async handleDataPeittoalueet(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()
            const options = {
                fillColor: "#009FE366",
                fillOpacity: 0.8,
                color: "#666",
                opacity: 0.8,
                weight: 1,
                fill: true,
                stroke: true
            }

            data.koordinaatit.options = options
            data.koordinaatit.handleOnClick = false // ei haluta korostaa alueita kun käyttäjä klikkaa karttaa
            const rightSlotData = new DynamicRightSlotData("1")
            rightSlotData.municipality = " "
            rightSlotData.dataUpdatedDate = this.getUpdatedtext(data.julkaisuaika ?? moment().toISOString()) ?? "?"
            store.dispatch("setRightSlotData", rightSlotData)

            apiData.push(new ApiDataModel("1", true, data.koordinaatit, data, rightSlotData, null))

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    // #endregion
    getUpdatedtext(time: string): string {
        let updatedTime = "?"
        if (moment(time).format("MM/YYYY")) {
            updatedTime = t("App.Posti.DataUpdated") + " " + moment(time).format("MM/YYYY")
        }
        return updatedTime
    }

    // #region TVSaatavuus
    async handleDataSaatavuus(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()
            const rightSlotData = new DynamicRightSlotData("1")

            if (data.length > 0) {
                const tableValues: Map<string[], boolean> = new Map()
                data.forEach((item) => {
                    item.suunta = t(item.suunta) ?? item.suunta

                    tableValues.set([item.nimi, item.suunta + " " + item.etaisyys.toString() + " km", item.kanavalkm.toString()], false)
                    rightSlotData.dataUpdatedDate = this.getUpdatedtext(item.julkaisuaika ?? moment().toISOString()) ?? "?"
                })

                rightSlotData.tableData = new TableData(this.tableColumHeaders, data, tableValues)
            }
            store.dispatch("setRightSlotData", rightSlotData)

            apiData.push(new ApiDataModel("1", true, null, data, rightSlotData, null))
            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    get tableColumHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.TVSaatavuus.Nimi") ?? "App.TVSaatavuus.Nimi")
        columns.push(t("App.TVSaatavuus.Etaisyys") ?? "App.TVSaatavuus.Etaisyys")
        columns.push(t("App.TVSaatavuus.KanavienLukumäärä") ?? "App.TVSaatavuus.KanavienLukumäärä")
        return columns
    }

    get currentLanguage() {
        return store.getters.activeLang
    }

    get modalHeader(): string {
        return store.getters.header
    }

    async handleDataTVKanavat(data): Promise<void> {
        try {
            // const result = ConvertTVKanavat.toPalvelutarjojaJaKanavat(data)

            let textContent = ""
            let url = ""
            // TODO: Voiko palveluntarjoajia olla enemmän kuin yksi? Tämä toimii silloin väärin.
            // TODO: Kanavien järjestäminen kanavanumeron mukaan.
            textContent = t("App.TVSaatavuus.Palveluntarjoaja") ?? ""
            if (data && data[0]) {
                switch (this.currentLanguage) {
                    case "fi":
                        url = data[0].urlfi
                        break
                    case "sv":
                        url = data[0].urlsv
                        break
                    case "en":
                        url = data[0].urlen
                        break
                    default:
                        url = data[0].urlfi
                        break
                }
                textContent += "<a href=\"" + url + "\" target='_blank'>" + data[0].nimi
                    + " <img class='iframe-external-icon' src='" + this.getExternalLink() + "' alt='"
                    + this.getExternalLinkAlt() + "'>" + utils.getLinkOpensNewTabSpan() + "</a>"
            }
            // näytetään kaikki kanavat modaalin taulukossa
            let tableRowData = new Array<Array<string>>()
            let tvKanavat = new Array<TVKanavat>()
            tvKanavat = []
            data.forEach((value) => {
                if (value.kanavalista && value.kanavalista.length > 0) {
                    tvKanavat = tvKanavat.concat(value.kanavalista)
                }
            })
            tableRowData = ConvertTVKanavat.toTable(tvKanavat)
            const tableData = new TableData(this.modalTableColumHeaders, tableRowData)
            // avataan modaali tarkempien tietojen näyttämiseksi
            const modalData = new DynamicModalData("tvkanavat", this.modalHeader, textContent, null, tableData)
            store.dispatch("setModalData", modalData)
            this.openModal()
        }
        catch (err) {
            console.log(err)
        }
    }

    getExternalLink(): string {
        return store.getters.contentServiceUrl + "/Content/icons/svg-blue/linkki-external.svg" + "?" + store.getters.contentServiceTimeStamp
    }

    getExternalLinkAlt(): string | undefined {
        return t("App.Link.UlosJohtavaLinkki")
    }

    openModal() {
        store.dispatch("setModalComponentName", "AntenniTVSaatavuusModal")
    }

    get modalTableColumHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.TVSaatavuus.Kanava.Nimi") ?? "App.TVSaatavuus.Kanava.Nimi")
        columns.push(t("App.TVSaatavuus.Kanava.Numero") ?? "App.TVSaatavuus.Kanava.Numero")
        columns.push(t("App.TVSaatavuus.Kanava.Nippu") ?? "App.TVSaatavuus.Kanava.Nippu")
        columns.push(t("App.TVSaatavuus.Kanava.Taajuus") ?? "App.TVSaatavuus.Kanava.Taajuus")
        return columns
    }
    // #endregion

    // #region RadioKuuluvuus
    async handleDataKuuluvuus(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()

            const rightSlotData = new DynamicRightSlotData("1")
            if (data.length > 0) {
                const tableValues: string[][] = []
                data.forEach((item) => {
                    tableValues.push([item.asema, item.taajuus, item.lahetin, item.palveluntarjoaja])
                    rightSlotData.dataUpdatedDate = this.getUpdatedtext(item.julkaisuaika ?? moment().toISOString()) ?? "?"
                })
                rightSlotData.tableData = new TableData(this.tableColumHeadersForRadioKuuluvuus, tableValues)
            }
            store.dispatch("setRightSlotData", rightSlotData)

            apiData.push(new ApiDataModel("1", true, null, data, rightSlotData, null))
            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    get tableColumHeadersForRadioKuuluvuus(): string[] {
        const columns: string[] = []
        columns.push(t("App.RadioKuuluvuus.Asema") ?? "App.RadioKuuluvuus.Asema")
        columns.push(t("App.RadioKuuluvuus.Taajuus") ?? "App.RadioKuuluvuus.Taajuus")
        columns.push(t("App.RadioKuuluvuus.Lahetin") ?? "App.RadioKuuluvuus.Lahetin")
        columns.push(t("App.RadioKuuluvuus.Palveluntarjoaja") ?? "App.RadioKuuluvuus.Palveluntarjoaja")
        return columns
    }
    // #endregion
}
